// src/views/Operaciones/LiquidacionClientes/ListarLiquidacionClientes.jsx
import React, { useEffect, useState } from "react";
// import { FaEye } from "react-icons/fa"; // No se usa aún, se puede agregar si se necesita columna acción
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import FiltroView from "./FiltroLiquidacionDeCLientes";
import VisualizarView from "./VerLiquidacionDeClientes";

// ============================
// CONFIG
// ============================
const DEFAULT_PAGE_SIZE = 30;

// ============================
// UTILS
// ============================
const safeNum = (v) => (v === null || v === undefined || v === "" ? 0 : v);
const fmt = new Intl.NumberFormat("es-PY");
const fmtNum = (v) => fmt.format(safeNum(v));

// ============================
// COMPONENTE
// ============================
export default function ListarLiquidacionClientes() {
  // ============================
  // ESTADO LISTA
  // ============================
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);

  // ============================
  // FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  const limpiarSeleccion = () => {
    const empty = {};
    setFiltros(empty);
    setPaginaActual(1);
    fetchConsulta(1, porPagina, empty);
  };

  // ============================
  // MODAL VER
  // ============================
  const [showView, setShowView] = useState(false);
  const [idView, setIdView] = useState(null);
  useEscClose(() => setShowView(false), showView);

  // ============================
  // NORMALIZAR FILTROS
  // ============================
  const normalizarFiltros = (f) => ({
    ...f,
    clienteId: f?.cliente?.id ?? f?.clienteId,
    productoId: f?.producto?.id ?? f?.productoId,
    liquidacionEjecucionId:
      f?.liquidacionEjecucion?.id ?? f?.liquidacionEjecucionId,
    estadoId: f?.estado?.id ?? f?.estadoId,
    ivaId: f?.iva?.id ?? f?.ivaId,
  });

  // ============================
  // FETCH
  // ============================
  const fetchConsulta = async (
    page = paginaActual,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);

    try {
      const params = new URLSearchParams();
      params.set("page", String(page - 1));
      params.set("size", String(size));

      const filtrosNorm = normalizarFiltros(filtrosAplicados || {});

      Object.entries(filtrosNorm).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        if (typeof v === "object") return;
        const s = String(v).trim();
        if (s === "") return;
        params.set(k, s);
      });

      const url = `${apiRoutes.liquidacionCliente.list}?${params.toString()}`;
      const data = await appFetchJson(url);

      setItems(data.items || []);
      setTotalBackend(data.totalElements || 0);
    } catch (e) {
      console.error("Error cargando liquidación de clientes", e);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  // ============================
  // INIT
  // ============================
  useEffect(() => {
    fetchConsulta(1, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================
  // PAGINACIÓN
  // ============================
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));

  const cambiarPagina = (num) => {
    const pagina = Math.min(Math.max(num, 1), totalPaginas);
    setPaginaActual(pagina);
    fetchConsulta(pagina, porPagina);
  };

  // ============================
  // DEFINICIÓN DE COLUMNAS (DynamicTable)
  // ============================
  const columns = [
    {
      header: "Cliente",
      accessor: "clienteNombre",
      type: "text",
      width: "12%",
      cellClass: "break-words",
      render: (row) => row.clienteNombre ?? "—",
    },
    {
      header: "Producto",
      accessor: "productoNombre",
      type: "text",
      width: "12%",
      cellClass: "break-words",
      render: (row) => row.productoNombre ?? "—",
    },
    {
      header: "Estado",
      accessor: "estadoNombre",
      type: "text",
      width: "8%",
      render: (row) => row.estadoNombre ?? "—",
    },
    {
      header: "Liquidación Ejecucion",
      type: "center",
      width: "8%",
      render: (row) => row.liquidacionEjecucionId ?? "—",
    },
    {
      header: "IVA",
      accessor: "ivaNombre",
      type: "text",
      width: "6%",
      render: (row) => row.ivaNombre ?? "—",
    },
    {
      header: "Valor Total",
      type: "center",
      width: "8%",
      render: (row) => fmtNum(row.valorTotal),
    },
    {
      header: "Descuento Por PPOL",
      type: "center",
      width: "10%",
      render: (row) => fmtNum(row.valorTotalDescPetr),
    },
    {
      header: "Descuento Por EE.SS.",
      type: "center",
      width: "10%",
      render: (row) => fmtNum(row.valorTotalDescEess),
    },
    {
      header: "Valor Libe",
      type: "center",
      width: "8%",
      render: (row) => fmtNum(row.valorTotalLibe),
    },
    {
      header: "Total IVA",
      type: "center",
      width: "8%",
      render: (row) => fmtNum(row.valorTotalIva),
    },
    {
      header: "Cantidad de Producto",
      type: "center",
      width: "8%",
      render: (row) => fmtNum(row.qnProducto),
    },
    {
      header: "Precio",
      type: "center",
      width: "8%",
      render: (row) => fmtNum(row.valorPrecio),
    },
  ];

  // ============================
  // RENDER
  // ============================
  return (
    <div className="page">
      <ListHeader
        title="Liquidación de Clientes"
        onFilterClick={() => setMostrarFiltros(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchConsulta(1, size, filtros);
        }}
      />

      {/* FILTROS */}
      {mostrarFiltros && (
        <FiltroView
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchConsulta(1, porPagina, nuevosFiltros);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* VER */}
      {showView && (
        <VisualizarView id={idView} onClose={() => setShowView(false)} />
      )}

      {/* INDICADOR DE CARGA */}
      {cargando && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Cargando datos...
        </div>
      )}

      {/* TABLA RESPONSIVA */}
      {!cargando && (
        <DynamicTable
          size="L" // Breakpoint en 1210px para 12 columnas
          data={items}
          columns={columns}
        />
      )}

      {/* PAGINADOR */}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />
    </div>
  );
}